import { Button, Callout, Modal, Tooltip } from '@dev-spendesk/grapes';
import React, { useState } from 'react';

import { useTranslation } from 'common/hooks/useTranslation';

import { TransferToMemberAutocompleteContainer } from '../../containers/TransferToMemberAutocompleteContainer';
import { getMemberName, type Member } from '../../models/member';

type Props = {
  onCancel: () => void;
  onSubmit: (transferringMemberId: string | undefined) => void;
  member: Member;
  companyName: string;
};

export const MemberDeleteModal = ({
  onCancel,
  onSubmit,
  member,
  companyName,
}: Props) => {
  const { t } = useTranslation('global');
  const [transferringMemberId, setTransferringMemberId] = useState<
    string | undefined
  >();
  const [
    noTransferringMemberIdSelectedError,
    setNoTransferringMemberIdSelectedError,
  ] = useState<string | undefined>();
  const [isScreeningLoading, setIsScreeningLoading] = useState(false);
  const [screeningResultUsername, setScreeningResultUsername] = useState('');

  return (
    <Modal
      title={t('members.deleteModal.title', {
        name: getMemberName(member),
        companyName,
      })}
      actions={[
        <Button
          text={t('members.deleteModal.buttonCancel')}
          variant="secondaryNeutral"
          onClick={onCancel}
          key="cancel"
        />,
        <Tooltip
          content={t('subscriptions.panelDetails.loadingTransferUserDetails')}
          triggerAsChild
          isDisabled={!isScreeningLoading}
          key="submit"
        >
          <Button
            text={t('members.deleteModal.buttonSubmit')}
            variant="primaryWarning"
            isDisabled={isScreeningLoading || !!screeningResultUsername}
            onClick={async () => {
              if (!transferringMemberId && member.subscriptionsCount > 0) {
                setNoTransferringMemberIdSelectedError(
                  t('members.deleteModal.transferringMemberError'),
                );
              } else {
                await onSubmit(transferringMemberId);
                onCancel();
              }
            }}
          />
        </Tooltip>,
      ]}
      isOpen
      iconName="triangle-warning"
      iconVariant="warning"
    >
      <div className="text-left">
        <Callout
          variant="warning"
          iconName="triangle-warning"
          title={t('members.deleteModal.warning', {
            name: getMemberName(member),
            companyName,
          })}
        />
        <div className="mt-24 text-content-secondary-bg-primary">
          <div>{t('members.deleteModal.noteTitle')}</div>
          <ul className="ml-16 list-disc">
            <li>{t('members.deleteModal.note1', { companyName })}</li>
            <li>{t('members.deleteModal.note2')}</li>
          </ul>
        </div>
        {member.subscriptionsCount > 0 && (
          <div className="mt-32">
            <TransferToMemberAutocompleteContainer
              onSelectMemberId={setTransferringMemberId}
              toDeleteMember={member}
              error={noTransferringMemberIdSelectedError}
              setIsScreeningLoading={setIsScreeningLoading}
              screeningResultUsername={screeningResultUsername}
              setScreeningResultUsername={setScreeningResultUsername}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};
